<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { api } from '../services/api';
import { useAuth } from '../stores/auth';

const auth = useAuth();
const loading = ref(true);
const saving = ref(false);
const submitting = ref(false);
const error = ref('');
const success = ref('');
const membershipRole = ref('MEMBER');
const company = ref<any>(null);
const form = ref<any>({});
const isOwner = computed(() => membershipRole.value === 'OWNER');
const isRejectedExporter = computed(() => company.value?.type === 'EXPORTER' && company.value?.status === 'REJECTED');
const businessTypes = [
  { title: 'Fabricante', value: 'MANUFACTURER' }, { title: 'Productor', value: 'PRODUCER' },
  { title: 'Distribuidor', value: 'DISTRIBUTOR' }, { title: 'Comercializador', value: 'TRADER' }, { title: 'Otro', value: 'OTHER' }
];
const experienceOptions = [
  { title: 'Sin experiencia exportadora', value: 'NONE' }, { title: 'Menos de 1 año', value: 'LESS_THAN_1_YEAR' },
  { title: '1 a 3 años', value: 'ONE_TO_THREE_YEARS' }, { title: '3 a 5 años', value: 'THREE_TO_FIVE_YEARS' },
  { title: 'Más de 5 años', value: 'MORE_THAN_FIVE_YEARS' }
];
function statusLabel(status: string) { return ({ APPROVED: 'Aprobada', PENDING: 'Pendiente de revisión', REJECTED: 'Rechazada' } as Record<string, string>)[status] || status; }
function normalize(value: unknown) { const text = String(value ?? '').trim(); return text || null; }
async function load() {
  loading.value = true; error.value = '';
  try {
    const response = (await api.get('/company')).data;
    company.value = response.company; membershipRole.value = response.membershipRole;
    form.value = { ...response.company, categoriesText: (response.company.categories || []).join(', ') };
  } catch (requestError: any) { error.value = requestError.response?.data?.error || 'No fue posible cargar la empresa.'; }
  finally { loading.value = false; }
}
async function save() {
  if (!isOwner.value) return;
  saving.value = true; error.value = ''; success.value = '';
  try {
    const payload = {
      name: form.value.name, phone: normalize(form.value.phone), city: normalize(form.value.city), state: normalize(form.value.state),
      website: normalize(form.value.website), descriptionOriginal: normalize(form.value.descriptionOriginal),
      categories: String(form.value.categoriesText || '').split(',').map((item: string) => item.trim()).filter(Boolean),
      businessType: form.value.businessType || null, exportExperience: form.value.exportExperience || null
    };
    const response = (await api.patch('/company', payload)).data;
    company.value = response.company; form.value = { ...response.company, categoriesText: (response.company.categories || []).join(', ') };
    await auth.load(); success.value = 'Los datos de la empresa se guardaron correctamente.';
  } catch (requestError: any) { error.value = requestError.response?.data?.message || requestError.response?.data?.error || 'No fue posible guardar los cambios.'; }
  finally { saving.value = false; }
}
async function submitForReview() {
  submitting.value = true; error.value = ''; success.value = '';
  try { await api.post('/company/submit-review'); await load(); await auth.load(); success.value = 'La empresa fue reenviada para revisión.'; }
  catch (requestError: any) { error.value = requestError.response?.data?.error || 'No fue posible reenviar la empresa.'; }
  finally { submitting.value = false; }
}
onMounted(load);
</script>

<template>
  <v-container class="py-8" style="max-width: 900px">
    <h1>Mi empresa</h1>
    <v-alert v-if="error" type="error" class="mt-4" closable @click:close="error=''">{{ error }}</v-alert>
    <v-alert v-if="success" type="success" class="mt-4" closable @click:close="success=''">{{ success }}</v-alert>
    <v-card class="mt-5 pa-5" :loading="loading">
      <template v-if="company">
        <div class="d-flex align-center flex-wrap ga-3 mb-5"><strong>Estado:</strong><v-chip :color="company.status==='APPROVED'?'success':company.status==='REJECTED'?'error':'warning'">{{ statusLabel(company.status) }}</v-chip><span v-if="!isOwner" class="text-medium-emphasis">Solo el propietario puede editar estos datos.</span></div>
        <v-alert v-if="company.rejectionReason" type="warning" class="mb-5">Motivo de rechazo: {{ company.rejectionReason }}</v-alert>
        <v-row>
          <v-col cols="12" md="8"><v-text-field v-model="form.name" label="Nombre de la empresa" :disabled="!isOwner"/></v-col>
          <v-col cols="12" md="4"><v-text-field v-model="form.phone" label="Teléfono" :disabled="!isOwner"/></v-col>
          <v-col cols="12" md="6"><v-text-field v-model="form.city" label="Ciudad" :disabled="!isOwner"/></v-col>
          <v-col cols="12" md="6"><v-text-field v-model="form.state" label="Región / estado" :disabled="!isOwner"/></v-col>
          <v-col cols="12"><v-text-field v-model="form.website" label="Sitio web" placeholder="https://empresa.com" :disabled="!isOwner"/></v-col>
          <v-col cols="12"><v-textarea v-model="form.descriptionOriginal" label="Descripción" counter="5000" :disabled="!isOwner"/></v-col>
          <v-col cols="12"><v-text-field v-model="form.categoriesText" label="Categorías" hint="Sepáralas por coma" persistent-hint :disabled="!isOwner"/></v-col>
          <v-col cols="12" md="6"><v-select v-model="form.businessType" label="Tipo de negocio" :items="businessTypes" clearable :disabled="!isOwner"/></v-col>
          <v-col cols="12" md="6"><v-select v-model="form.exportExperience" label="Experiencia exportadora" :items="experienceOptions" clearable :disabled="!isOwner"/></v-col>
        </v-row>
        <v-card-actions class="px-0 pt-4"><v-btn v-if="isOwner" color="primary" :loading="saving" @click="save">Guardar cambios</v-btn><v-btn v-if="isOwner && isRejectedExporter" color="secondary" :loading="submitting" @click="submitForReview">Reenviar a revisión</v-btn></v-card-actions>
      </template>
    </v-card>
  </v-container>
</template>
